
import { parseWithZod } from '@conform-to/zod'
import { type ActionFunctionArgs } from '@remix-run/node'
import { useState } from 'react';
import {
	LegalPageContainer,
	LegalPageLink,
	LegalPageParagraph,
	LegalPageSectionTitle,
	LegalPageTitle,
} from '#app/components/layout/legal'
import { LogoHome } from '#app/components/logo'
import {
	type IFeedbackSentiment,
} from '#app/models/feedback._._definitions.ts'
import { FeedbackSchema } from '#app/models/feedback._._schema.ts'
import { mapFeedbackFields } from '#app/models/feedback._._utils.ts'
import { dbCreateFeedback } from '#app/models/feedback._.create.db.server.ts'
import { getFullUrl } from '#app/utils/env.server.ts'
import {
	respondWithCreated,
	respondWithError,
	respondWithSubmission,
} from '#app/utils/request-helpers.js'
import { buildSlackNotificationBlocks } from '#app/utils/slack-blocks.server.ts'
import { sendSlackMessage } from '#app/utils/slack.server.ts'
import { FeedbackForm, getSentimentEmoji, SLACK_TARGET_CHANNEL } from '../resources+/feedback-dialog';

const feedbackDescription =
	'We would love to hear what went well or how we can improve the product experience.'
const feedbackDescriptionSuccess =
	'Your feedback is very important to us. Thank you for taking the time to share your thoughts!'

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const submission = parseWithZod(formData, {
		schema: FeedbackSchema,
	})

	if (submission.status !== 'success') {
		return respondWithSubmission(submission)
	}

	const { content, sentiment } = submission.value

	let createdFeedback
	try {
		createdFeedback = await dbCreateFeedback({
			data: {
				content,
				sentiment,
			},
			select: {
				id: true,
				content: true,
				sentiment: true,
			},
		})
	} catch (error) {
		console.error('Database error creating feedback:', error)
		// Consider returning a more specific error response
		return respondWithError(
			submission,
			'Failed to save feedback due to a database issue.',
		)
	}

	if (createdFeedback) {
		console.info('Constructing feedback Slack message')

		// --- Slack Notification Logic ---
		if (SLACK_TARGET_CHANNEL) {
			const feedback = mapFeedbackFields(createdFeedback)
			const userAdminUrl = getFullUrl(`/admin/users/users/${feedback.userId}`)
			const feedbackAdminUrl = getFullUrl(`/admin/feedback`)
			const sentimentEmoji = getSentimentEmoji(
				feedback.sentiment as IFeedbackSentiment,
			)

			// Use the reusable block builder
			const blocks = buildSlackNotificationBlocks({
				headerText: `New Feedback Received`,
				mainContent: feedback.content,
				contextFields: [
					{
						type: 'mrkdwn',
						text: `*Sentiment:*\n${sentimentEmoji} ${feedback.sentiment}`,
					},
					{
						type: 'mrkdwn',
						text: `*From User:*\n<${userAdminUrl}|${feedback.userId}>`,
					},
					{
						type: 'mrkdwn',
						text: `*Feedback Link:*\n<${feedbackAdminUrl}|View Feedback>`,
					},
				],
				useSectionFields: true, // Use section with fields for feedback
			})

			// Fallback text is crucial for notifications and when blocks can't render
			const fallbackText = `New feedback (${getSentimentEmoji(feedback.sentiment as IFeedbackSentiment)} ${feedback.sentiment}) from user ${feedback.userId}: ${feedback.content.substring(0, 150)}...`

			console.info('Sending Slack message')
			await sendSlackMessage({
				channel: SLACK_TARGET_CHANNEL,
				blocks,
				text: fallbackText,
			})
		} else {
			console.warn(
				'Neither SLACK_BOT_TOKEN & SLACK_TARGET_CHANNEL are set. Skipping Slack notification.',
			)
		}
	}

	return createdFeedback
		? respondWithCreated(submission)
		: respondWithError(submission, 'Failed to submit feedback')
}

export default function SupportRoute() {
	const [formSuccess, setFormSuccess] = useState(false)

	return (
		<LegalPageContainer>
			<LogoHome />
			<LegalPageTitle>Choros Support</LegalPageTitle>

			<LegalPageParagraph>
				Thank you for your interest in Choros ("the App") and reaching out to Choros Support. This page is here to help you navigate any issues or questions you may have while using the App as part of
				the Alpha testing program.
			</LegalPageParagraph>

			<LegalPageSectionTitle>Help</LegalPageSectionTitle>
			<LegalPageParagraph>
				If you have any issues, please contact us at{' '}
				<LegalPageLink href="mailto:alex@chorosapp.com">
					alex@chorosapp.com
				</LegalPageLink>
				.
			</LegalPageParagraph>

			<LegalPageSectionTitle>Leave Feedback</LegalPageSectionTitle>
			<LegalPageParagraph>
				{formSuccess
					? feedbackDescriptionSuccess
					: feedbackDescription}
			</LegalPageParagraph>
			{!formSuccess && <FeedbackForm onSuccess={() => setFormSuccess(true)} formAction='/support' />}
		</LegalPageContainer>
	)
}
